import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import TaskModal from '../components/TaskModal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, Plus, CheckCircle2, Calendar as CalendarIcon, AlertTriangle, Sparkles } from 'lucide-react';
import { tasksAPI } from '../lib/api';
import { toast } from 'sonner';

const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const PRIORITY_DOT = { low: 'bg-cyan-500', medium: 'bg-amber-500', high: 'bg-red-500' };
const PRIORITY_LABEL = { low: 'Low', medium: 'Med', high: 'High' };

const TaskCalendar = () => {
  const today = useMemo(() => new Date(), []);
  const todayIso = fmtDate(today);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | done
  const [scope, setScope] = useState('mine'); // mine | all
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [editingTask, setEditingTask] = useState(null);

  const reload = useCallback(async () => {
    try {
      const params = scope === 'mine' ? { mine: true } : {};
      const res = await tasksAPI.list(params);
      setTasks(res.data || []);
      window.dispatchEvent(new Event('tasks-changed'));
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { reload(); }, [reload]);

  // Build 6-week Monday-first grid
  const monthGrid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
    const gridStart = new Date(year, month, 1 - firstWeekday);
    const days = [];
    for (let i = 0; i < 42; i++) {
      days.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
    }
    return days;
  }, [cursor]);

  const filteredTasks = useMemo(
    () => tasks.filter((t) => statusFilter === 'all' || t.status === statusFilter),
    [tasks, statusFilter]
  );

  const tasksByDate = useMemo(() => {
    const map = {};
    monthGrid.forEach((d) => { map[fmtDate(d)] = []; });
    filteredTasks.forEach((t) => {
      if (!t.due_date) return;
      if (map[t.due_date] !== undefined) map[t.due_date].push(t);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => {
        const pr = { high: 0, medium: 1, low: 2 };
        return (pr[a.priority] ?? 9) - (pr[b.priority] ?? 9);
      })
    );
    return map;
  }, [filteredTasks, monthGrid]);

  const monthStats = useMemo(() => {
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const inMonth = (iso) => {
      if (!iso) return false;
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      return dt >= monthStart && dt <= monthEnd;
    };
    return {
      total: filteredTasks.filter((t) => inMonth(t.due_date)).length,
      overdue: filteredTasks.filter((t) => t.status === 'pending' && t.due_date && t.due_date < todayIso).length,
      dueToday: filteredTasks.filter((t) => t.status === 'pending' && t.due_date === todayIso).length,
    };
  }, [filteredTasks, cursor, todayIso]);

  const openCreate = (dateIso) => {
    setEditingTask(null);
    setModalDate(dateIso);
    setModalOpen(true);
  };
  const openEdit = (task) => {
    setEditingTask(task);
    setModalDate(task.due_date);
    setModalOpen(true);
  };

  const onComplete = async (e, taskId) => {
    e.stopPropagation();
    try {
      await tasksAPI.complete(taskId);
      toast.success('Task completed');
      reload();
    } catch {
      toast.error('Failed to complete task');
    }
  };

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  const isToday = (d) => fmtDate(d) === todayIso;
  const inCurrentMonth = (d) => d.getMonth() === cursor.getMonth();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="task-calendar-page" className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">Task Calendar</h1>
            <p className="text-gray-500 mt-1">Click any day to add a task. Tasks generate an email reminder on their due date.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="task-scope" className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mine">My Tasks</SelectItem>
                <SelectItem value="all">All Tasks</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="task-status-filter" className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="done">Done</SelectItem>
              </SelectContent>
            </Select>
            <Button
              data-testid="calendar-today-btn"
              variant="outline"
              size="sm"
              onClick={goToday}
             
            >
              Today
            </Button>
            <Button
              data-testid="add-task-btn"
              onClick={() => openCreate(todayIso)}
              
            >
              <Plus className="w-4 h-4 mr-2" /> New Task
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="py-4 px-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Overdue</p>
                <p className="text-xl font-semibold text-ica-navy" data-testid="stat-overdue">{monthStats.overdue}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Due Today</p>
                <p className="text-xl font-semibold text-ica-navy" data-testid="stat-due-today">{monthStats.dueToday}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-ica-gold/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-ica-gold" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Tasks this month</p>
                <p className="text-xl font-semibold text-ica-navy" data-testid="stat-month-total">{monthStats.total}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calendar */}
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b px-5 py-3 bg-gray-50">
              <button data-testid="calendar-prev" onClick={goPrev} className="p-2 hover:bg-white rounded-md transition-colors text-ica-navy">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="font-heading text-lg font-semibold text-ica-navy" data-testid="calendar-title">
                {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
              </h2>
              <button data-testid="calendar-next" onClick={goNext} className="p-2 hover:bg-white rounded-md transition-colors text-ica-navy">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-7 border-b bg-gray-50/50">
              {WEEKDAYS.map((wd) => (
                <div key={wd} className="py-2 px-2 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  {wd}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {monthGrid.map((d) => {
                const iso = fmtDate(d);
                const dayTasks = tasksByDate[iso] || [];
                const visible = dayTasks.slice(0, 3);
                const overflow = dayTasks.length - visible.length;
                const muted = !inCurrentMonth(d);
                const todayHighlight = isToday(d);
                return (
                  <div
                    key={iso}
                    data-testid={`calendar-day-${iso}`}
                    onClick={() => openCreate(iso)}
                    className={`relative min-h-[100px] border-b border-r p-1.5 transition-colors group cursor-pointer ${
                      muted ? 'bg-gray-50/50' : 'bg-white'
                    } hover:bg-ica-gold/5`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`inline-flex items-center justify-center text-xs font-medium ${
                          todayHighlight
                            ? 'w-6 h-6 rounded-full bg-ica-navy text-white'
                            : muted ? 'text-gray-400' : 'text-gray-700'
                        }`}
                      >
                        {d.getDate()}
                      </span>
                      <button
                        data-testid={`add-task-${iso}`}
                        onClick={(e) => { e.stopPropagation(); openCreate(iso); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-ica-navy hover:text-white text-ica-navy"
                        title="Add task"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      {visible.map((t) => (
                        <div
                          key={t.id}
                          onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                          title={`${t.title}${t.related_label ? ` — ${t.related_label}` : ''}`}
                          className={`flex items-center gap-1 text-[10.5px] rounded px-1 py-0.5 ${
                            t.status === 'done'
                              ? 'bg-emerald-50 text-emerald-700 line-through'
                              : t.due_date < todayIso
                              ? 'bg-red-50 text-red-700'
                              : 'bg-ica-navy/5 text-gray-800'
                          } hover:ring-1 hover:ring-ica-gold cursor-pointer`}
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_DOT[t.priority] || 'bg-gray-400'}`} />
                          <span className="truncate flex-1">{t.title}</span>
                          {t.status === 'pending' && (
                            <button
                              data-testid={`complete-task-${t.id}`}
                              onClick={(e) => onComplete(e, t.id)}
                              className="opacity-0 group-hover:opacity-100 hover:text-emerald-600"
                              title="Mark as done"
                            >
                              <CheckCircle2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                      {overflow > 0 && (
                        <div className="text-[10px] text-gray-500 font-medium">+{overflow} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-4 px-5 py-3 border-t bg-gray-50 text-xs text-gray-600">
              <span className="font-medium text-gray-500">Priority:</span>
              {Object.entries(PRIORITY_LABEL).map(([k, label]) => (
                <span key={k} className="inline-flex items-center gap-1.5">
                  <span className={`inline-block w-2 h-2 rounded-full ${PRIORITY_DOT[k]}`} />
                  {label}
                </span>
              ))}
              <span className="ml-auto text-gray-400">Tip: click any day to add a task</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <TaskModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        task={editingTask}
        defaultDate={modalDate}
        onSaved={() => reload()}
        onDeleted={() => reload()}
      />
    </Layout>
  );
};

export default TaskCalendar;
